/**
 * Orchestrates chatbot analytics loads (workspace-scoped reporting API).
 */

import { botReportingApiClient } from "@/api/botReportingApiClient";
import { reportingBotDeriver } from "@/pages/analytics/lib/reportingBotDeriver";
import { ReportingAnalyticsDateRange } from "@/pages/analytics/lib/reportingAnalyticsDateRange";
import { BotWordCloudViewModel } from "@/pages/analytics/lib/BotWordCloudViewModel";
import { BotReportedIssuesAggregator } from "@/pages/analytics/lib/BotReportedIssuesAggregator";

const SCOPE = "ReportingBotService";

export class ReportingBotService {
  constructor(apiClient = botReportingApiClient, deriver = reportingBotDeriver) {
    this.apiClient = apiClient;
    this.deriver = deriver;
    this.wordCloud = new BotWordCloudViewModel();
    this.issues = new BotReportedIssuesAggregator();
  }

  _unwrap(payload, label) {
    if (!payload || typeof payload !== "object") {
      return { ok: false, error: `${label}: invalid response` };
    }
    const o = /** @type {Record<string, unknown>} */ (payload);
    if (o.error) {
      return { ok: false, error: String(o.error) };
    }
    return { ok: true, data: payload };
  }

  /**
   * @param {() => Promise<unknown>} fn
   * @param {string} label
   */
  async _safeCall(fn, label) {
    try {
      const payload = await fn();
      const result = this._unwrap(payload, label);
      if (!result.ok) {
        this.logError(new Error(result.error));
        return null;
      }
      return result.data;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      this.logError(err);
      return null;
    }
  }

  async load(range, signal) {
    const window = new ReportingAnalyticsDateRange(range);
    const init = signal ? { signal } : {};

    const [
      dailyChats,
      completion,
      avgChatTime,
      escalation,
      csat,
      thumbs,
      rating,
      sentiment,
      trending,
      wordTrends,
      negativeComments,
    ] = await Promise.all([
      this._safeCall(() => this.apiClient.getDailyChats(range, init), "dailychats"),
      this._safeCall(() => this.apiClient.getCompletionRate(range, init), "completion_rate"),
      this._safeCall(() => this.apiClient.getAvgChatTime(range, init), "avg_chat_time"),
      this._safeCall(() => this.apiClient.getEscalationRate(range, init), "escalation_rate"),
      this._safeCall(() => this.apiClient.getCsatScore(range, init), "csat_score"),
      this._safeCall(() => this.apiClient.getThumbsFeedbackStats(range, init), "thumbs_feedback_stats"),
      this._safeCall(() => this.apiClient.getRatingDistribution(range, init), "rating_distribution"),
      this._safeCall(() => this.apiClient.getSentimentDashboard(range, init), "sentiment/dashboard"),
      this._safeCall(() => this.apiClient.getTrendingTopics(range, { limit: 15 }, init), "trending-topics"),
      this._safeCall(
        () => this.apiClient.getUserWordFrequencyTrends(range, { limit: 25 }, init),
        "user-word-frequency-trends",
      ),
      this._safeCall(
        () => this.apiClient.getNegativeComments(range, { limit: 50 }, init),
        "negative_comments",
      ),
    ]);

    if (!dailyChats && !completion && !escalation) {
      throw new Error(
        "Could not load chatbot metrics. Check reporting-service logs and tenant schema (epoch-ms sessions).",
      );
    }

    const view = this.deriver.buildViewModel({
      dailyChats: dailyChats ?? { total: 0, change: 0, data: {} },
      unique: {},
      returning: {},
      avgChatTime: avgChatTime ?? { avg_time_minutes: 0, change: 0 },
      completion: completion ?? { rate: 0, change: 0, total_completions: 0 },
      dropoff: {},
      fallback: {},
      escalation: escalation ?? { rate: 0, change: 0, by_reason: {} },
      csat: csat ?? { score: 0, change: 0 },
      thumbs: thumbs ?? {},
      rating: rating ?? {},
      sentiment: sentiment ?? { data: { sentiment_counts: {} } },
      trending: trending ?? { trending_topics: [] },
      periodStart: window.startDate,
      periodEnd: window.endDate,
    });
    const wordRows = Array.isArray(wordTrends?.trending_words)
      ? wordTrends.trending_words
      : view.trendingTopics;
    const comments = Array.isArray(negativeComments?.comments) ? negativeComments.comments : [];
    return {
      ...view,
      wordCloud: this.wordCloud.build(wordRows.length ? wordRows : view.trendingTopics),
      reportedIssues: this.issues.aggregate(comments),
    };
  }

  logError(err) {
    if (typeof console !== "undefined" && console.error) {
      console.error(`[${SCOPE}]`, { message: err?.message ?? String(err) });
    }
  }
}

export const reportingBotService = new ReportingBotService();
